import type { ComponentType } from "react";
import Head from "next/head";
import { Anchor, Box, Container, Stack, Text, Title } from "@mantine/core";
import {
  IconAperture,
  IconArrowsShuffle,
  IconBinaryTree,
  IconEyeCheck,
  IconGavel,
} from "@tabler/icons-react";
import { SiteShell } from "../components/SiteShell";
import styles from "../styles/faq.module.css";

type FaqIcon = ComponentType<{ size?: number; stroke?: number }>;

type FaqItem = {
  id: string;
  question: string;
  paragraphs: string[];
  bottomLine: string;
  icon: FaqIcon;
};

const FAQ_ITEMS: FaqItem[] = [
  {
    id: "llm-agnostic",
    question: "Is Cassandra tied to one LLM?",
    paragraphs: [
      "Cassandra applies large language models in specific program pathways to amplify an operator’s ability to extract key details from complex data.",
      "The system is LLM-agnostic by design. Value resides in the application layer—viewpoint adaptation, corpus enrichment, constraints, and auditability—not in a model.",
    ],
    bottomLine: "Cassandra is LLM-agnostic—the value is the application layer, not the model.",
    icon: IconArrowsShuffle,
  },
  {
    id: "hallucinations",
    question: "Does Cassandra hallucinate?",
    paragraphs: [
      "Cassandra employs a battery of unique safeguards, such as the Viewpoint creation system, that compound the rejection of false outputs by up to 98% for industry-leading extraction.",
      "Additionally, all reasoning is user-inspectable, ensuring 100% auditability.",
    ],
    bottomLine: "Cassandra rejects false outputs using user-defined key extraction parameters.",
    icon: IconEyeCheck,
  },
  {
    id: "domain-adaptation",
    question: "How fast can Cassandra adapt to a new research domain?",
    paragraphs: [
      "Cassandra’s Viewpoint system enables rapid scientific domain adaptation via explicit user-defined inputs—write in natural language the scientific domain and what specific data you want pulled from your documents, and the Viewpoint system will do the rest.",
    ],
    bottomLine: "Define the scientific domain. Specify what details are most meaningful to you. Press enter.",
    icon: IconAperture,
  },
  {
    id: "rag-vs-graph",
    question: "How is this different from a basic RAG chatbot?",
    paragraphs: [
      "RAG systems retrieve relevant passages and get lost in short order (Laban et al., 2025). Cassandra represents the next evolution, constructing structured relationships across documents and exposing the exact subgraph used to produce an answer, along with traceable reasoning paths for inspection and validation.",
    ],
    bottomLine: "RAG retrieves text. Cassandra reasons over structure at scale for dense and contextually nuanced work.",
    icon: IconBinaryTree,
  },
  {
    id: "audit-heavy",
    question: "Can this support audit-heavy environments?",
    paragraphs: [
      "Cassandra ensures compliance through evidence-linked outputs, inspectable reasoning paths, and layer-to-layer fail-safe controls. The system supports flexible deployment across cloud images, on-premises infrastructure, and SCIF-ready desktop environments.",
    ],
    bottomLine: "Inspectable reasoning. Evidence-linked outputs. Deployable anywhere—cloud, on-prem, or SCIF.",
    icon: IconGavel,
  },
];

const accentClasses = [styles.accentRed, styles.accentBlue, styles.accentGreen, styles.accentYellow] as const;

export default function FaqPage() {
  const title = "FAQ";
  const description = "Answers to common questions about Cassandra.";

  return (
    <SiteShell>
      <Head>
        <title>{title} | Legaché</title>
        <meta name="description" content={description} />
      </Head>

      <Box className={styles.page}>
        <Box component="section" id="top" className={styles.hero}>
          <Box aria-hidden="true" className={styles.heroPattern} />
          <Container size="xl" className={styles.heroInner}>
            <Box className={styles.heroGrid}>
              <Stack gap={18} className={styles.heroCopy}>
                <Title order={1} className={styles.heroTitle}>
                  {title}
                </Title>
              </Stack>

              <Box component="nav" aria-label="FAQ sections" className={styles.toc}>
                <Text className={styles.tocTitle}>On this page</Text>
                <Box className={styles.tocList}>
                  {FAQ_ITEMS.map((item, index) => (
                    <Anchor key={item.id} href={`#${item.id}`} className={styles.tocLink}>
                      <Text component="span" className={styles.tocIndex}>
                        {String(index + 1).padStart(2, "0")}
                      </Text>
                      <Text component="span">{item.question}</Text>
                    </Anchor>
                  ))}
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>

        <Box component="section" className={styles.faqSection}>
          <Container size="xl">
            <Box className={styles.faqList}>
              {FAQ_ITEMS.map((item, index) => {
                const ItemIcon = item.icon;
                const accentClass = accentClasses[index % accentClasses.length];

                return (
                  <Box
                    key={item.id}
                    component="article"
                    id={item.id}
                    className={`${styles.faqItem} ${accentClass}`}
                  >
                    <Box className={styles.questionColumn}>
                      <Box className={styles.questionMeta}>
                        <Box className={styles.questionIcon}>
                          <ItemIcon size={21} stroke={1.9} />
                        </Box>
                        <Text component="span" className={styles.questionIndex}>
                          {String(index + 1).padStart(2, "0")}
                        </Text>
                      </Box>
                      <Title order={2} className={styles.questionTitle}>
                        {item.question}
                      </Title>
                    </Box>

                    <Stack gap={22} className={styles.answerColumn}>
                      <Stack gap={14} className={styles.answerCopy}>
                        {item.paragraphs.map((paragraph) => (
                          <Text key={paragraph} component="p">
                            {paragraph}
                          </Text>
                        ))}
                      </Stack>

                      <Box className={styles.bottomLine}>
                        <Text className={styles.bottomLineLabel}>Bottom line</Text>
                        <Text component="p">{item.bottomLine}</Text>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          </Container>
        </Box>
      </Box>
    </SiteShell>
  );
}
